import { Injectable, Injector, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
    GoogleAuthProvider, User, onAuthStateChanged,
    sendSignInLinkToEmail, signInWithEmailAndPassword,
    signInWithEmailLink, signInWithPopup, signOut
} from 'firebase/auth';
import { FIREBASE_AUTH } from './firebase.auth';
import { doc } from 'firebase/firestore';
import { FIREBASE_FIRESTORE, observeDocumentSnapshots } from './firebase.firestore';
import { BehaviorSubject, EMPTY, Observable, catchError, filter, finalize, firstValueFrom, from, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { LocalStorageService } from '../core/localStorageService';
import { CurrentUserDto, EmailDto, MceApiService, RoleDto, SubscriptionDto, UserDto } from '../core/mceapi.client.generated';
import { appOpt } from '../core/mceapi.client.helpers';
import { MAX_DAYS_LOGGED_IN } from '../shared/constants';
import { FirebaseEventService } from './firebase.event.service';
import { DialogService } from '../shared/dialogs/dialog.service';
import { DateHelpers } from '../shared/functions/date.helpers';
import { Option, fromNullable, match, none } from '../shared/functions/functional';

const currentUrl = () => new URL(window.location.href);
const returnUrl = () => {
  const url = currentUrl();
  const basePath = currentUrl().pathname.substring(0, url.pathname.lastIndexOf('/signin') + 1);
  url.pathname = `${basePath}signin-complete`;
  return url;
}

export type SendSignInLinkResult = 'sent' | 'login-disabled';
export type UserSignInResult = 'signed-in' | 'login-disabled';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {

  private readonly firebaseAuth = inject(FIREBASE_AUTH);
  private readonly localStorage = inject(LocalStorageService);
  private readonly dialogService = inject(DialogService);
  private readonly firebaseEventService = inject(FirebaseEventService);
  private readonly firestore = inject(FIREBASE_FIRESTORE);
  private readonly mceApiService = inject(MceApiService);
  private readonly injector = inject(Injector);


  // ----------------------------------------------------------------
  // Firebase User State
  // ----------------------------------------------------------------
  readonly user = toSignal<User | null | undefined>(
    new Observable<User | null>(subscriber => onAuthStateChanged(
      this.firebaseAuth,
      user => subscriber.next(user),
      error => subscriber.error(error),
      () => subscriber.complete(),
    )),
    { initialValue: undefined }
  );

  // ----------------------------------------------------------------
  // Current User (API data)
  // ----------------------------------------------------------------

  // null - not signed in, undefined - not yet initialized
  readonly currentUserState = signal<CurrentUserDto | null | undefined>(undefined);

  // Firebase account associated with the last successful current-user response.
  private readonly currentUserLoadedForAuthUid = signal<string | undefined>(undefined);

  readonly currentUserMatchesSignedInAccount = computed(() => {
    const uid = this.user()?.uid;
    return !!uid && this.currentUserLoadedForAuthUid() === uid && !!this.currentUserState()?.user?.uid;
  });

  readonly currentUser = computed<Option<UserDto>>(() =>
    fromNullable(this.currentUserState()?.user));

  readonly currentUserRoles = computed<RoleDto[]>(() =>
    this.currentUserState()?.roles ?? []);

  readonly currentUserSubscription = computed<SubscriptionDto | undefined>(() =>
    this.currentUserState()?.subscription);

  readonly currentUserProfileComplete = computed<boolean>(() =>
    this.currentUserState()?.profileComplete?.value ?? false);

  // ----------------------------------------------------------------
  // Initialization State
  // ----------------------------------------------------------------
  private _initialized = new BehaviorSubject<boolean>(false);
  initialized$ = this._initialized.asObservable();
  initialized = toSignal(this.initialized$, { initialValue: false });

  userReady$ = toObservable(this.currentUserState, { injector: this.injector }).pipe(
    filter(v => v !== undefined),   // resolved (null or dto)
    map(() => true),
    shareReplay(1)
  );

  // ----------------------------------------------------------------
  // Session Expiration
  // ----------------------------------------------------------------
  readonly isExpired = computed(() => {
    const u = this.firebaseAuth.currentUser;
    if (!u?.metadata?.lastSignInTime) return false;
    const last = new Date(u.metadata.lastSignInTime);
    return DateHelpers.dateDiffInDays(new Date(), last) > MAX_DAYS_LOGGED_IN;
  });


  // ----------------------------------------------------------------
  // Token
  // ----------------------------------------------------------------
  getIdToken(force = false): Promise < string > {
    const u = this.firebaseAuth.currentUser;
    return u ? u.getIdToken(force) : Promise.resolve('');
  }

  // ----------------------------------------------------------------
  // Authentication Methods
  // ----------------------------------------------------------------

  async sendSignInLink(email: string): Promise<SendSignInLinkResult> {
    const dto = await firstValueFrom(this.mceApiService.v1TokenEmail({ email }));

    if (dto.isLoginEnabled !== true) {
      this.localStorage.clearEmailForSignIn();
      return 'login-disabled';
    }

    const token = dto.emailToken;
    if (!token) throw new Error('Email sign-in token was not returned by the API.');

    this.localStorage.setEmailSignInToken(token);

    try {
      await sendSignInLinkToEmail(
        this.firebaseAuth,
        email,
        { url: returnUrl().href, handleCodeInApp: true }
      );
      return 'sent';
    } catch (error: any) {
      console.error('Error sending email link:', error);
      this.dialogService
        .acknowledge('Error retrieving sign-in link', `${error.code}: ${error.message}`, '320px')
        .subscribe();
      throw error;
    }
  }

  async resolveStoredEmailForSignIn(): Promise<Option<string>> {
    const emailToken = this.localStorage.getEmailSignInToken();
    if (!emailToken) return none;

    try {
      const emailOptionDto = await firstValueFrom(
        this.mceApiService.v1TokenEmailValidate(emailToken)
      );

      return match(
        () => none,
        (x: EmailDto) => fromNullable(x.email)
      )(appOpt(emailOptionDto));
    } catch {
      return none;
    }
  }

  async completeSignInWithLink(link: string, emailOverride?: string): Promise<void> {
    const email = emailOverride ?? match(
      () => { throw new Error('No email found for sign-in link.'); },
      (x: string) => x
    )(await this.resolveStoredEmailForSignIn());

    try {
      if (this.firebaseAuth.currentUser) {
        await signOut(this.firebaseAuth);
        this.currentUserState.set(null);
      }

      await signInWithEmailLink(this.firebaseAuth, email, link);

      this.localStorage.clearEmailForSignIn();
      this.firebaseEventService.logSignInEvent('email_link');
    } catch (error: any) {
      this.dialogService
        .acknowledge('Error signing in', `${error.code}: ${error.message}`, '320px')
        .subscribe();

      throw error;
    }
  }

  signInWithEmailAndPassword(email: string, password: string): Observable<UserSignInResult> {
    return this.mceApiService.v1TokenEmail({ email }).pipe(
      switchMap(dto => {
        if (dto.isLoginEnabled !== true || dto.isEmailPasswordLoginEnabled !== true) {
          return of<UserSignInResult>('login-disabled');
        }

        const promise = signInWithEmailAndPassword(this.firebaseAuth, email, password);

        return from(promise).pipe(
          tap(() => this.firebaseEventService.logSignInEvent('password')),
          map((): UserSignInResult => 'signed-in')
        );
      }),
      catchError(error => {
        console.error('Error completing email and password signin:', error);
        this.dialogService.acknowledge('Error signing in', `${error.code}: ${error.message}`, '320px').subscribe();
        throw error;
      })
    );
  }

  signOut(): Observable<void> {
    const promise = signOut(this.firebaseAuth);
    return from(promise).pipe(
      tap(() => {
        this.currentUserState.set(null);
      })
    );
  }

  signInWithGoogle(): Observable<UserSignInResult> {
    const provider = new GoogleAuthProvider();
    let firebaseSignedIn = false;

    this.blockRefresh(true);

    const promise = signInWithPopup(this.firebaseAuth, provider);

    return from(promise).pipe(
      tap(() => firebaseSignedIn = true),
      switchMap(credential => {
        const email = credential.user.email;
        if (!email) {
          return this.signOut().pipe(
            map((): UserSignInResult => 'login-disabled')
          );
        }

        return this.mceApiService.v1TokenEmail({ email }).pipe(
          switchMap(dto => {
            if (dto.isLoginEnabled !== true) {
              return this.signOut().pipe(
                map((): UserSignInResult => 'login-disabled')
              );
            }

            this.blockRefresh(false);
            return this.refreshCurrentUser().pipe(
              tap(() => this.firebaseEventService.logSignInEvent('google')),
              map((): UserSignInResult => 'signed-in')
            );
          })
        );
      }),
      catchError(error => {
        console.error('Error with Google sign-in:', error);

        const errorMessage = error.code === 'auth/popup-closed-by-user'
          ? 'Sign-in was cancelled'
          : `${error.code}: ${error.message}`;

        this.dialogService.acknowledge(
          'Google Sign-in Error',
          errorMessage,
          '320px'
        ).subscribe();

        if (!firebaseSignedIn) {
          return throwError(() => error);
        }

        return this.signOut().pipe(
          catchError(signOutError => {
            console.error('Error signing out after Google sign-in failure:', signOutError);
            return of(undefined);
          }),
          switchMap(() => throwError(() => error))
        );
      }),
      finalize(() => this.blockRefresh(false))
    );
  }


  /// --------------------------------------------------------------------
  /// Refresh Current User
  /// --------------------------------------------------------------------

  private _blockRefresh = signal(false);
  blockRefresh(value:boolean) {
    this._blockRefresh.set(value);
  }

 /// Refreshes the current user data from the API.
 /// Call this after profile updates to ensure the local state is in sync.
  refreshCurrentUser(isCurrentSession: () => boolean = () => true): Observable<CurrentUserDto> {

    if (this._blockRefresh()) {
      return EMPTY;
    }

    const sessionUid = this.firebaseAuth.currentUser?.uid;

    return this.mceApiService.v1UsersCurrentuser().pipe(
      filter(() => (this.firebaseAuth.currentUser?.uid) === sessionUid && isCurrentSession()),
      tap(dto => {
        this.currentUserLoadedForAuthUid.set(sessionUid);
        this.currentUserState.set(dto);
      }),
      catchError(error => {
        console.error('Error refreshing current user:', error);
        throw error;
      })
    );
  }


  constructor() {
    toObservable(this.user, { injector: this.injector }).pipe(
      switchMap(u => {
        if (u === undefined) return EMPTY;
        if (!u) {
          this.currentUserState.set(null);
          return EMPTY;
        }

        const ref = doc(this.firestore, `users/${u.uid}`);
        return observeDocumentSnapshots(ref).pipe(
          switchMap(() => this.refreshCurrentUser().pipe(
            catchError(err => {
              console.error('Error refreshing current user from Firestore trigger', err);
              this.currentUserState.set(null);
              return EMPTY;
            })
          )),
          catchError(err => {
            console.error('Firestore trigger failed', err);
            return EMPTY;
          })
        );
      }),
      takeUntilDestroyed(),
    ).subscribe();



    effect(() => {
      const cu = this.currentUserState(); // undefined | null | CurrentUserDto
      this._initialized.next(!!cu);  // Emit true ONLY when we have a non-null DTO (roles available).
    });

  }

}

